import { apiRequest, refreshSession } from '../api/client';
import { useSessionStore } from '../auth/session-store';

export const DELIVERY_NOTE_STATUSES = [
  'PENDING',
  'DISPATCHED',
  'DELIVERED',
  'SIGNED',
  'INVOICED',
  'VOID',
] as const;
export type DeliveryNoteStatus = (typeof DELIVERY_NOTE_STATUSES)[number];

/** Matches `apps/api` `StoredDeliveryNoteSignature` — never `scannedKey` (design.md §7.4 decision 6). */
export interface DeliveryNoteSignature {
  id: string;
  receiverName: string;
  receiverDocument: string | null;
  receiverRole: string | null;
  signedAt: string;
  serverReceivedAt: string;
  needsReview: boolean;
}

/** Matches `apps/api` `StoredDeliveryNote`. Never `unitPriceCents`/`vatRate` (portal-delivery-notes/spec.md: "NO DEBE incluir unitPriceCents"). */
export interface DeliveryNote {
  id: string;
  noteNumber: string;
  siteId: string;
  siteNameSnapshot: string;
  serviceDate: string;
  status: DeliveryNoteStatus;
  voidReason: string | null;
  signature: DeliveryNoteSignature | null;
}

export interface DeliveryNoteSearchParams {
  serviceDateFrom?: string | undefined;
  serviceDateTo?: string | undefined;
  siteId?: string | undefined;
  status?: DeliveryNoteStatus | undefined;
  page: number;
  pageSize: number;
}

export interface DeliveryNoteSearchResult {
  items: DeliveryNote[];
  page: number;
  pageSize: number;
  total: number;
}

/** Matches `apps/api` `GET /portal/delivery-notes`. */
export function listDeliveryNotes(
  params: DeliveryNoteSearchParams,
): Promise<DeliveryNoteSearchResult> {
  const query = new URLSearchParams();

  if (params.serviceDateFrom !== undefined) {
    query.set('serviceDateFrom', params.serviceDateFrom);
  }
  if (params.serviceDateTo !== undefined) {
    query.set('serviceDateTo', params.serviceDateTo);
  }
  if (params.siteId !== undefined) {
    query.set('siteId', params.siteId);
  }
  if (params.status !== undefined) {
    query.set('status', params.status);
  }
  query.set('page', String(params.page));
  query.set('pageSize', String(params.pageSize));

  return apiRequest<DeliveryNoteSearchResult>(`/portal/delivery-notes?${query.toString()}`);
}

export interface RequestSignedScanDownloadUrlResult {
  downloadUrl: string;
  expiresAt: string;
}

/** Matches `apps/api` `GET /portal/delivery-notes/:id/signed-scan`: a short-lived presigned URL, never the raw S3 key. */
export function requestSignedScanDownloadUrl(
  id: string,
): Promise<RequestSignedScanDownloadUrlResult> {
  return apiRequest<RequestSignedScanDownloadUrlResult>(`/portal/delivery-notes/${id}/signed-scan`);
}

function sendBlobRequest(path: string, init: RequestInit): Promise<Response> {
  const token = useSessionStore.getState().session?.accessToken;
  const headers = new Headers(init.headers);

  if (token !== undefined) {
    headers.set('authorization', `Bearer ${token}`);
  }

  return fetch(`/api/v1${path}`, { ...init, headers, credentials: 'include' });
}

/**
 * Triggers a browser download for a PDF response, same single-flight
 * 401-refresh-and-retry criterion `apps/web-admin`'s `downloadPdf` already
 * applies: a bare `fetch()` with no refresh would leave a concurrently
 * rotated access token as an unrecovered 401 and the download would never
 * start.
 */
export async function downloadDeliveryNotePdf(id: string, noteNumber: string): Promise<void> {
  let response = await sendBlobRequest(`/portal/delivery-notes/${id}/pdf`, { method: 'GET' });

  if (response.status === 401) {
    const renewed = await refreshSession();

    if (renewed) {
      response = await sendBlobRequest(`/portal/delivery-notes/${id}/pdf`, { method: 'GET' });
    }
  }

  if (!response.ok) {
    throw new Error('No pudimos descargar el PDF del remito.');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `remito-${noteNumber}.pdf`;
  link.click();

  setTimeout(() => URL.revokeObjectURL(url), 0);
}
